import BASE_URL from "../../config/api";
import axios from "axios";
import { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BsCheckCircleFill, BsSearch, BsClipboard, BsClipboardCheck } from "react-icons/bs";

const AllPurchases = () => {
  const [loading, setLoading] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [search, setSearch] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const copyRef = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/payment/purchases`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` },
      });
      setPurchases(res.data.data ?? []);
    } catch {
      toast.error("Failed to load purchases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = purchases.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.email?.toLowerCase().includes(q) ||
      p.planRef?.toLowerCase().includes(q) ||
      p.transactionRef?.toLowerCase().includes(q) ||
      p.name?.toLowerCase().includes(q)
    );
  });

  const totalRevenue = purchases.reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div>
      <ToastContainer />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchases</h1>
          <p className="text-gray-500 text-sm mt-1">
            {purchases.length} transaction{purchases.length !== 1 ? "s" : ""} &nbsp;·&nbsp;
            <span className="text-[#1877F2] font-semibold">
              ₦{totalRevenue.toLocaleString()} total
            </span>
          </p>
        </div>
        <button
          onClick={load}
          className="text-sm text-[#1877F2] border border-[#1877F2]/30 hover:bg-[#1877F2]/5 font-semibold px-4 py-2 rounded-full transition-colors duration-200"
        >
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <BsSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email, plan ref, name…"
          className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#1877F2]/20 focus:border-[#1877F2]"
        />
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
              <div className="flex gap-4">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-1/5 ml-auto" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 text-gray-400">
          <p className="text-5xl mb-4">💳</p>
          <p className="text-lg font-semibold">{search ? "No matching purchases" : "No purchases yet"}</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Plan</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ref</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((p) => (
                  <tr key={p._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-4 text-gray-500 whitespace-nowrap">
                      {new Date(p.createdAt).toLocaleDateString("en-NG", {
                        day: "2-digit", month: "short", year: "numeric",
                      })}
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-gray-900">{p.name || "—"}</p>
                      <p className="text-gray-400 text-xs">{p.email}</p>
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-xs font-bold text-[#1877F2] bg-[#1877F2]/10 px-2 py-0.5 rounded-full">
                        {p.planRef}
                      </span>
                      <p className="text-gray-600 text-xs mt-1 line-clamp-1 max-w-[200px]">{p.planDescription}</p>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-xs text-gray-500 break-all">{p.transactionRef}</p>
                        <button
                          onClick={() => copyRef(p.transactionRef, p._id)}
                          title="Copy reference"
                          className="flex-shrink-0 text-gray-400 hover:text-[#1877F2] transition-colors"
                        >
                          {copiedId === p._id ? <BsClipboardCheck size={13} className="text-green-500" /> : <BsClipboard size={13} />}
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right font-bold text-gray-900 whitespace-nowrap">
                      ₦{(p.amount || 0).toLocaleString()}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-2.5 py-1 rounded-full">
                        <BsCheckCircleFill size={10} /> Paid
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map((p) => (
              <div key={p._id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{p.name || "Unknown"}</p>
                    <p className="text-gray-400 text-xs">{p.email}</p>
                  </div>
                  <span className="font-bold text-[#1877F2] text-base">₦{(p.amount || 0).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-[#1877F2] bg-[#1877F2]/10 px-2 py-0.5 rounded-full">{p.planRef}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    <BsCheckCircleFill size={9} /> Paid
                  </span>
                  <span className="text-gray-400 text-xs ml-auto">
                    {new Date(p.createdAt).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <p className="font-mono text-[10px] text-gray-400 break-all flex-1">{p.transactionRef}</p>
                  <button
                    onClick={() => copyRef(p.transactionRef, p._id)}
                    title="Copy reference"
                    className="flex-shrink-0 text-gray-400 hover:text-[#1877F2] transition-colors"
                  >
                    {copiedId === p._id ? <BsClipboardCheck size={12} className="text-green-500" /> : <BsClipboard size={12} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default AllPurchases;
